import { useCreditBalanceQuery } from '../subscription.query';
import { subscriptionStore } from '../subscription.store';
import { daysUntil } from '../usage-format';

import type { CreditLimits } from '@littlebird/sdk';

export const USAGE_WARNINGS = [
  { pct: 70, minDaysToReset: 7 },
  { pct: 90, minDaysToReset: 2 },
] as const;

const HEAVY_ROUTINE_SHARE_PCT = 15;

const SPARE_CREDIT_FLOOR = 200;

const clampPct = (pct: number) => Math.max(0, Math.min(100, pct));

const toPct = (part: number, whole: number) =>
  clampPct(Math.round((100 * part) / whole));

const floorPct = (part: number, whole: number) =>
  clampPct(Math.floor((100 * part) / whole));

/** Credits the user can still spend this cycle. `remaining_credits` already carries the purchased pool. */
export const getSpendablePool = (creditLimits: CreditLimits) =>
  creditLimits.used_credits + creditLimits.remaining_credits;

export const useCreditUsage = () => {
  const { creditLimits, liteModeActive, creditBalance } =
    subscriptionStore.state;
  const hasAllowance =
    !!creditLimits &&
    creditLimits.allowance_credits > 0 &&
    creditLimits.plan_grant_kind === 'monthly';

  const hasUsageFigure = hasAllowance && !liteModeActive;

  return {
    usedPct: hasAllowance
      ? floorPct(creditLimits.used_credits, creditLimits.allowance_credits)
      : 0,
    spendableUsedPct: hasAllowance
      ? floorPct(creditLimits.used_credits, getSpendablePool(creditLimits))
      : 0,
    hasSpareCredits: creditBalance >= SPARE_CREDIT_FLOOR,
    resetsAt: creditLimits?.resets_at,
    daysUntilReset: creditLimits ? daysUntil(creditLimits.resets_at) : 0,
    hasUsageFigure,
  };
};

export const useRoutineCreditShare = (routineId: number) => {
  const { data } = useCreditBalanceQuery();

  const { liteModeActive } = subscriptionStore.state;
  const allowance =
    !liteModeActive && data?.plan_grant_kind === 'monthly'
      ? data.plan_grant_amount
      : 0;
  const routine = data?.spend_by_routine_cycle?.find(
    (row) => row.routine_id === String(routineId)
  );
  const sharePct =
    allowance > 0 && routine ? toPct(routine.credits, allowance) : 0;

  return {
    sharePct,
    runCount: routine?.count ?? 0,
    isHeavy: sharePct > HEAVY_ROUTINE_SHARE_PCT,
    hasAllowance: allowance > 0,
  };
};
